/**
 * Assemble configured build + user filters into OpenSearch clauses.
 *
 * Shared by the tabular report builder and the dashboard widgets so both apply
 * the exact same filter semantics:
 *   - relative_date presets resolved to concrete gte/lte ranges first
 *   - build filters applied via their layout: top-level slots AND'd, OR groups
 *     wrapped in `bool.should` (minimum_should_match: 1)
 *   - user filters applied flatly (AND'd)
 *
 * Returns a flat list of clauses ready to drop into `bool.filter`. Callers that
 * need to divert some filters elsewhere (e.g. the report builder's HAVING /
 * bucket_selector path for aggregated metric columns) pass `skipKey` to exclude
 * those keys here and handle them separately.
 */

import { buildClausesForEntry, buildClausesForField } from './filter-clause-builder';
import type { FilterClauseContext } from './filter-clause-builder';
import { resolveRelativeDateFilters } from './relative-date-resolver';
import type { FilterLayout } from './filter-operators';
import type { FilterClause } from 'tio-common/utils/open-search-builder';

export interface ConfiguredFilters {
  buildFilterValues: Record<string, unknown>;
  buildFilterLayout: FilterLayout;
  userFilterValues: Record<string, unknown>;
}

export interface BuildFilterClausesOptions {
  /** Return true for a raw filter key handled elsewhere (e.g. HAVING). */
  skipKey?: (key: string) => boolean;
}

/**
 * Clauses for flat AND'd extra entries (dashboard drilldowns): the usual
 * suffix-keyed shape, plus `<field>__ranges`, an OR of half-open date ranges
 * for a selection that spans several date buckets.
 */
export function buildExtraFilterClauses(
  values: Record<string, unknown>,
  ctx: FilterClauseContext
): FilterClause[] {
  return Object.entries(values).flatMap(([key, value]) => {
    if (!key.endsWith('__ranges')) return buildClausesForEntry(key, value, ctx);
    const field = ctx.pathFor(key.replace(/__ranges$/, ''));
    const ranges = Array.isArray(value) ? (value as Array<{ gte: string; lt: string }>) : [];
    if (ranges.length === 0) return [];
    return [
      {
        bool: {
          should: ranges.map((r) => ({ range: { [field]: { gte: r.gte, lt: r.lt } } })),
          minimum_should_match: 1,
        },
      },
    ];
  });
}

export function buildConfiguredFilterClauses(
  filters: ConfiguredFilters,
  ctx: FilterClauseContext,
  options: BuildFilterClausesOptions = {}
): FilterClause[] {
  const skip = options.skipKey ?? (() => false);
  const resolvedBuild = resolveRelativeDateFilters(filters.buildFilterValues ?? {});
  const resolvedUser = resolveRelativeDateFilters(filters.userFilterValues ?? {});
  const clauses: FilterClause[] = [];

  // Build filters via layout: top-level slots AND'd, OR groups as bool.should.
  // `skipKey` (HAVING) only ever consumes TOP-LEVEL AND filters + user filters,
  // never OR-group members — so it applies to top-level slots only. Applying it
  // to group members would strip a build OR-group clause whenever the same
  // metric field also carries a user HAVING filter, silently narrowing the OR
  // group (e.g. build `(amount>10 OR region=west)` + user `amount>100` must
  // keep `amount>10` in the WHERE group).
  for (const slot of filters.buildFilterLayout ?? []) {
    if (slot.type === 'filter') {
      if (skip(slot.fieldKey)) continue;
      clauses.push(...buildClausesForField(slot.fieldKey, resolvedBuild, ctx));
    } else {
      const shouldClauses = slot.fieldKeys.flatMap((k) =>
        buildClausesForField(k, resolvedBuild, ctx)
      );
      if (shouldClauses.length > 0) {
        clauses.push({ bool: { should: shouldClauses, minimum_should_match: 1 } });
      }
    }
  }

  // User filters flatly (AND'd).
  for (const [key, value] of Object.entries(resolvedUser)) {
    if (skip(key)) continue;
    clauses.push(...buildClausesForEntry(key, value, ctx));
  }

  return clauses;
}
